// src/pages/SuperAdmin/PlansManager.jsx
import React, { useState } from 'react';
import { Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Badge } from 'reactstrap';
import { post, put, del } from 'helpers/api_helper';

const PlansManager = ({ plans, modules = [], loading, onRefresh }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    trial_days: 0,
    max_chairs: 2,
    max_doctors: 2,
    max_patients: 1000,
    max_storage_mb: 2048,
    features: [],
    is_popular: 0,
    is_active: 1,
    sort_order: 0,
  });

  const handleOpenCreate = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      price_monthly: 1999,
      price_yearly: 19990,
      trial_days: 0,
      max_chairs: 3,
      max_doctors: 3,
      max_patients: 5000,
      max_storage_mb: 5120,
      features: ['dental_chart', 'multi_chair', 'billing_accounts', 'patient_portal', 'daily_briefing'],
      is_popular: 0,
      is_active: 1,
      sort_order: (plans.length || 0) + 1,
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (plan) => {
    setEditingPlan(plan);
    let planFeatures = [];
    try {
      planFeatures = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || []);
    } catch (e) {
      planFeatures = [];
    }

    setFormData({
      name: plan.name,
      slug: plan.slug,
      description: plan.description || '',
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      trial_days: plan.trial_days || 0,
      max_chairs: plan.max_chairs,
      max_doctors: plan.max_doctors,
      max_patients: plan.max_patients,
      max_storage_mb: plan.max_storage_mb,
      features: Array.isArray(planFeatures) ? planFeatures : [],
      is_popular: plan.is_popular || 0,
      is_active: plan.is_active !== undefined ? plan.is_active : 1,
      sort_order: plan.sort_order || 0,
    });
    setIsModalOpen(true);
  };

  const handleToggleFeature = (featureKey) => {
    const current = [...(formData.features || [])];
    if (current.includes(featureKey)) {
      setFormData({ ...formData, features: current.filter((k) => k !== featureKey) });
    } else {
      setFormData({ ...formData, features: [...current, featureKey] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingPlan) {
        await put(`/superadmin/plans/${editingPlan.id}`, formData);
      } else {
        await post('/superadmin/plans', formData);
      }
      setIsModalOpen(false);
      onRefresh();
    } catch (err) {
      alert(err?.message || 'Error saving subscription plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this subscription plan?')) return;
    try {
      await del(`/superadmin/plans/${planId}`);
      onRefresh();
    } catch (err) {
      alert(err?.message || 'Failed to delete plan');
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h5 className="font-size-16 font-weight-bold text-dark mb-1">
            <i className="bx bx-package text-warning me-2" /> Subscription Tiers & Module Control
          </h5>
          <p className="text-muted font-size-12 mb-0">
            Define pricing, doctor/chair quotas, and configure accessible clinical modules per plan.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1 font-weight-semibold shadow-sm"
          onClick={handleOpenCreate}
        >
          <i className="bx bx-plus-circle font-size-14" /> Create New Tier
        </button>
      </div>

      {/* Plan Cards Grid */}
      <Row className="mb-4">
        {plans.map((plan) => {
          let planFeatures = [];
          try {
            planFeatures = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || []);
          } catch (e) {
            planFeatures = [];
          }

          const isUnlimitedChairs = Number(plan.max_chairs) === -1;
          const isUnlimitedDocs = Number(plan.max_doctors) === -1;
          const isUnlimitedPatients = Number(plan.max_patients) === -1;

          return (
            <Col lg={3} md={6} key={plan.id} className="mb-4">
              <div className={`plan-card ${plan.is_popular ? 'popular' : ''}`}>
                {plan.is_popular ? (
                  <span className="plan-badge">MOST POPULAR</span>
                ) : (
                  <span className="badge bg-light text-muted border font-size-10 mb-2 align-self-start">
                    TIER #{plan.sort_order || plan.id}
                  </span>
                )}

                <h4 className="plan-title">{plan.name}</h4>
                <p className="text-muted font-size-11 mb-2" style={{ minHeight: 34 }}>
                  {plan.description || 'No description provided'}
                </p>

                <div className="plan-price">
                  ₹{Number(plan.price_monthly).toLocaleString()}
                  <span>/month</span>
                </div>

                <div className="plan-limits">
                  <div className="limit-item">
                    <span>Chairs:</span>
                    <strong>{isUnlimitedChairs ? 'Unlimited' : `${plan.max_chairs} Chairs`}</strong>
                  </div>
                  <div className="limit-item">
                    <span>Doctors:</span>
                    <strong>{isUnlimitedDocs ? 'Unlimited' : `${plan.max_doctors} Doctors`}</strong>
                  </div>
                  <div className="limit-item">
                    <span>Patient Capacity:</span>
                    <strong>{isUnlimitedPatients ? 'Unlimited' : `${plan.max_patients.toLocaleString()}`}</strong>
                  </div>
                  <div className="limit-item">
                    <span>Cloud Storage:</span>
                    <strong>{(plan.max_storage_mb / 1024).toFixed(1)} GB</strong>
                  </div>
                </div>

                <div className="font-weight-semibold font-size-11 text-muted text-uppercase mb-2">
                  Enabled Feature Modules:
                </div>

                <ul className="plan-features-list">
                  {modules.map((m) => {
                    const isEnabled = planFeatures.includes(m.key);
                    return (
                      <li key={m.key} className={isEnabled ? '' : 'disabled'}>
                        <i className={`bx ${isEnabled ? 'bx-check-circle' : 'bx-x-circle'}`} />
                        <span>{m.label}</span>
                      </li>
                    );
                  })}
                </ul>

                <div className="border-top pt-3 d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark flex-grow-1 font-size-12"
                    onClick={() => handleOpenEdit(plan)}
                  >
                    <i className="bx bx-edit me-1" /> Edit Tier
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger font-size-12"
                    onClick={() => handleDeletePlan(plan.id)}
                  >
                    <i className="bx bx-trash" />
                  </button>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      {/* Plan Edit / Create Modal */}
      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} size="lg" centered>
        <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
          <i className="bx bx-package text-warning me-2" />
          {editingPlan ? `Edit Tier: ${editingPlan.name}` : 'Create New Subscription Plan'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Plan Name *</Label>
                  <Input
                    type="text"
                    required
                    placeholder="e.g. Starter Clinic"
                    value={formData.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      const slug = name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
                      setFormData({ ...formData, name, slug: formData.slug || slug });
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Slug / System Key *</Label>
                  <Input
                    type="text"
                    required
                    placeholder="e.g. starter-clinic"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-3">
              <Label className="font-weight-semibold font-size-12">Description</Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="Brief summary of target clinic size and features"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </FormGroup>

            <Row>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Monthly Price (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    required
                    value={formData.price_monthly}
                    onChange={(e) => setFormData({ ...formData, price_monthly: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Yearly Price (₹) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    required
                    value={formData.price_yearly}
                    onChange={(e) => setFormData({ ...formData, price_yearly: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-weight-semibold font-size-12">Trial Duration (Days)</Label>
                  <Input
                    type="number"
                    value={formData.trial_days}
                    onChange={(e) => setFormData({ ...formData, trial_days: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="font-weight-semibold font-size-13 text-dark mb-2 border-top pt-3">
              Resource Quotas (-1 for Unlimited):
            </div>

            <Row>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Max Operatory Chairs</Label>
                  <Input
                    type="number"
                    value={formData.max_chairs}
                    onChange={(e) => setFormData({ ...formData, max_chairs: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Max Doctors Limit</Label>
                  <Input
                    type="number"
                    value={formData.max_doctors}
                    onChange={(e) => setFormData({ ...formData, max_doctors: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Max Patients Limit</Label>
                  <Input
                    type="number"
                    value={formData.max_patients}
                    onChange={(e) => setFormData({ ...formData, max_patients: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Max Staff / Employees</Label>
                  <Input
                    type="number"
                    value={formData.max_employees || 10}
                    onChange={(e) => setFormData({ ...formData, max_employees: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Max Clinic Branches</Label>
                  <Input
                    type="number"
                    value={formData.max_clinics || 3}
                    onChange={(e) => setFormData({ ...formData, max_clinics: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-3">
                  <Label className="font-size-12 font-weight-semibold">Cloud Storage (MB)</Label>
                  <Input
                    type="number"
                    value={formData.max_storage_mb}
                    onChange={(e) => setFormData({ ...formData, max_storage_mb: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex align-items-center justify-content-between mb-2 border-top pt-3">
              <div className="font-weight-semibold font-size-13 text-dark">
                Feature Modules in this Tier ({formData.features?.length || 0}/{modules.length || 12}):
              </div>
              <div className="d-flex gap-1.5">
                <button
                  type="button"
                  className="btn btn-xs btn-outline-dark"
                  onClick={() => setFormData({ ...formData, features: modules.map(m => m.key) })}
                >
                  Enable All
                </button>
                <button
                  type="button"
                  className="btn btn-xs btn-outline-secondary"
                  onClick={() => setFormData({ ...formData, features: [] })}
                >
                  Clear All
                </button>
              </div>
            </div>

            <Row className="mb-3">
              {modules.map((m) => {
                const checked = formData.features.includes(m.key);
                return (
                  <Col md={6} key={m.key} className="mb-2">
                    <div
                      className={`p-2 rounded border d-flex align-items-center gap-2 cursor-pointer transition ${
                        checked ? 'bg-warning-subtle border-warning' : 'bg-light'
                      }`}
                      onClick={() => handleToggleFeature(m.key)}
                      style={{ cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input mt-0"
                        checked={checked}
                        onChange={() => handleToggleFeature(m.key)}
                      />
                      <div>
                        <div className="font-weight-semibold font-size-12 text-dark">{m.label}</div>
                        <small className="text-muted font-size-11">{m.category}</small>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Plan Configuration'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default PlansManager;
